// Procedural-body tree walker — ProceduralBodyIndexing PRD Slice 0.3 scaffold.
//
// Mirrors the inner walk-loop of `parseAndWalk` (in `indexBuilder.ts`), but
// targets a `ParsedBuffer` produced from a procedural body (CREATE PROCEDURE
// / CREATE TRIGGER source) rather than a SELECT body. Iterates the buffer's
// top-level statements, filters to embedded SELECTs, and hands each to the
// existing `walkSelect` + `emitAliasQualifiedColumnRefs` + `emitBareColumnRefs`
// pipeline.
//
// Engine-agnostic — each per-engine grammar's parsed tree exposes embedded
// SELECTs via the same `documentStructure.statements` shape (whether sourced
// from the universal scanner or a future ANTLR-grammar-driven attachment
// path). DML-shaped statements (`insert | update | delete | merge`) are
// deferred to per-grammar slices: `walkSelect` today only handles SELECT
// shapes, and lifting it to DML is out of scope for this scaffold.
//
// Procedure-local declarations (`DECLARE @x INT`, `p_user_id` parameters,
// etc.) are excluded by construction — they're not classified as `select`
// by `coarseKind`, so the walker never reaches them.
//
// Returns `true` when at least one embedded SELECT was walked. The
// orchestrator (`indexProceduralListing`) records a `parse-failed`
// placeholder when this returns `false`, mirroring the view/TVF pipeline.

import type { LineIndex } from '../../../sql-grammar/document/lineIndex';
import type { ParseOptions } from '../../../sql-grammar/model';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type { ParsedBuffer, QueryStructure } from '../../../sql-resolver/types';

import type { IdentifierIndex } from '../../types';
import { emitAliasQualifiedColumnRefs, emitBareColumnRefs } from '../columnRefScanner';
import type { PersistedObjectListing } from '../objectBodyFetcher';
import { walkSelect } from '../viewWalker';

export function walkProceduralTree(
  buffer: ParsedBuffer,
  source: string,
  parseOptions: ParseOptions,
  listing: PersistedObjectListing,
  byteOffset: number,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): boolean {
  const selectStmts = buffer.documentStructure.statements.filter((s) => s.kind === 'select');
  if (selectStmts.length === 0) return false;
  let walked = false;
  for (const stmt of selectStmts) {
    let qs: QueryStructure;
    try {
      qs = extractQueryStructure(buffer, stmt);
    } catch {
      continue;
    }
    walkSelect(qs, listing, byteOffset, lineIdx, index);
    const alreadyEmitted = new Set<string>();
    emitAliasQualifiedColumnRefs(qs, source, parseOptions, listing, byteOffset, lineIdx, index, alreadyEmitted);
    emitBareColumnRefs(qs, source, parseOptions, listing, byteOffset, lineIdx, index, alreadyEmitted);
    walked = true;
  }
  return walked;
}
